import React, { useRef } from 'react';
import { MONO } from './register';
import { CURRENCY_MARKS, useTicker } from './primitives';

/* ---------------------------------------------------------------------------
   Hero phones — the personal app's answer to the Business hero deck. Three
   screens drawn in markup (home, a transfer in flight, a card), fanned out
   and taking turns at the front while the hero is on screen. The screens are
   static, so they are hoisted and built once per module, not per render.
   Figures are illustrative.
   --------------------------------------------------------------------------- */

const LABEL = `${MONO} text-[0.5rem] uppercase tracking-[0.1em] text-[#8a8a8a]`;

function StatusBar() {
  return (
    <div className="flex items-center justify-between px-5 pt-3 text-[0.5625rem] font-semibold text-[#171717]">
      <span>9:41</span>
      <span className="h-[1.1rem] w-[4.25rem] rounded-full bg-[#171717]" aria-hidden="true" />
      <span className="flex items-center gap-1" aria-hidden="true">
        <span className="h-1.5 w-3 rounded-sm bg-[#171717]" />
      </span>
    </div>
  );
}

const ROWS = [
  { code: 'USD', name: 'US dollar', sub: 'ACH · Wire', amount: '$2,940.18' },
  { code: 'EUR', name: 'Euro', sub: 'IBAN · SEPA', amount: '€1,120.00' },
  { code: 'USDC', name: 'USD Coin', sub: 'Stablecoin', amount: '220.32' },
];

const ACTIONS = ['Send', 'Receive', 'Exchange', 'Cards'];

const HOME_SCREEN = (
  <div className="flex h-full flex-col px-4 pb-4 pt-4">
    <span className={LABEL}>Total balance</span>
    <span className="mt-1 text-[1.6rem] font-medium leading-none tracking-[-0.03em] text-[#171717]">$4,280.50</span>
    <span className={`${MONO} mt-2 w-fit rounded-full bg-[#dcf5ea] px-2 py-1 text-[0.5rem] uppercase tracking-[0.08em] text-[#0f5f3f]`}>
      3 balances
    </span>
    <div className="mt-4 grid grid-cols-4 gap-1.5">
      {ACTIONS.map((a, i) => (
        <span key={a} className="flex flex-col items-center gap-1">
          <span
            className={`grid h-9 w-9 place-items-center rounded-full ${i === 0 ? 'bg-[#171717]' : 'bg-[#f1f1f1]'}`}
            aria-hidden="true"
          >
            <span className={`h-2.5 w-2.5 rounded-[3px] ${i === 0 ? 'bg-[#10b981]' : 'bg-[#bdbdbd]'}`} />
          </span>
          <span className="text-[0.5rem] text-[#565656]">{a}</span>
        </span>
      ))}
    </div>
    <span className={`${LABEL} mt-5`}>Accounts</span>
    <div className="mt-2 flex flex-col gap-1.5">
      {ROWS.map((r) => (
        <div key={r.name} className="flex items-center gap-2.5 rounded-2xl bg-[#f6f6f6] p-2.5">
          <img src={CURRENCY_MARKS[r.code]} alt="" width={28} height={28} className="h-7 w-7 shrink-0 rounded-full object-cover" />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-[0.625rem] font-medium text-[#171717]">{r.name}</span>
            <span className="text-[0.5rem] text-[#8a8a8a]">{r.sub}</span>
          </span>
          <span className={`${MONO} text-[0.5625rem] text-[#171717]`}>{r.amount}</span>
        </div>
      ))}
    </div>
  </div>
);

const STEPS = [
  { label: 'Sent', time: '09:41', done: true },
  { label: 'Converted', time: '09:41', done: true },
  { label: 'On its way', time: '09:43', done: true, live: true },
  { label: 'Delivered', time: '', done: false },
];

const TRANSFER_SCREEN = (
  <div className="flex h-full flex-col px-4 pb-4 pt-4">
    <span className={LABEL}>Transfer</span>
    <span className="mt-1 text-[1.35rem] font-medium leading-none tracking-[-0.03em] text-[#171717]">€500.00</span>
    <span className="mt-1.5 text-[0.5625rem] text-[#565656]">to Amélie Laurent · SEPA</span>
    <div className="mt-4 rounded-2xl bg-[#f6f6f6] p-3">
      <div className="flex items-center justify-between">
        <span className={LABEL}>Rate</span>
        <span className={`${MONO} text-[0.5625rem] text-[#171717]`}>1 USD = 0.92 EUR</span>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className={LABEL}>You pay</span>
        <span className={`${MONO} text-[0.5625rem] text-[#171717]`}>$543.48</span>
      </div>
      <span className={`${MONO} mt-3 block rounded-full bg-[#ffffff] px-2 py-1 text-center text-[0.45rem] uppercase tracking-[0.1em] text-[#565656]`}>
        Shown before you send
      </span>
    </div>
    <ol className="mt-4 flex flex-col">
      {STEPS.map((s, i) => (
        <li key={s.label} className="relative flex items-start gap-2.5 pb-3 last:pb-0">
          {i < STEPS.length - 1 ? (
            <span
              className={`absolute left-[0.3rem] top-3 h-full w-px ${STEPS[i + 1].done ? 'bg-[#10b981]' : 'bg-[#e2e2e2]'}`}
              aria-hidden="true"
            />
          ) : null}
          <span
            className={`relative mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full ${s.done ? 'bg-[#10b981]' : 'border border-[#cfcfcf] bg-[#ffffff]'}`}
            aria-hidden="true"
          >
            {s.live ? <span className="yl-ping absolute inset-0 animate-ping rounded-full bg-[#10b981] opacity-60" /> : null}
          </span>
          <span className="flex flex-1 items-center justify-between">
            <span className={`text-[0.5625rem] ${s.done ? 'font-medium text-[#171717]' : 'text-[#8a8a8a]'}`}>{s.label}</span>
            <span className={`${MONO} text-[0.5rem] text-[#8a8a8a]`}>{s.time}</span>
          </span>
        </li>
      ))}
    </ol>
  </div>
);

const SPEND = [
  { name: 'Spotify', amount: '−$10.99' },
  { name: 'Uber', amount: '−$18.40' },
  { name: 'Top up', amount: '+$200.00', credit: true },
];

const CARD_SCREEN = (
  <div className="flex h-full flex-col px-4 pb-4 pt-4">
    <span className={LABEL}>Cards</span>
    <div className="relative mt-2 aspect-[1.586] w-full overflow-hidden rounded-2xl bg-gradient-to-br from-[#0f7a55] via-[#10b981] to-[#6ee7b7] p-3 text-[#ffffff] shadow-[0_18px_30px_-18px_rgba(16,185,129,0.8)]">
      <span className="absolute -right-8 -top-10 h-28 w-28 rounded-full bg-[#ffffff]/15" aria-hidden="true" />
      <span className="text-[0.625rem] font-semibold tracking-[-0.01em]">Yunlen</span>
      <span className={`${MONO} absolute bottom-3 left-3 text-[0.5625rem] tracking-[0.14em]`}>•••• 4821</span>
      <span className="absolute bottom-2.5 right-3 flex -space-x-1.5" aria-hidden="true">
        <span className="h-4 w-4 rounded-full bg-[#ffffff]/80" />
        <span className="h-4 w-4 rounded-full bg-[#ffffff]/45" />
      </span>
    </div>
    <div className="mt-3 flex items-center justify-between">
      <span className={LABEL}>Card balance</span>
      <span className={`${MONO} text-[0.625rem] text-[#171717]`}>$318.60</span>
    </div>
    <div className="mt-3 grid grid-cols-2 gap-1.5">
      <span className="rounded-full bg-[#171717] py-2 text-center text-[0.5625rem] font-medium text-[#ffffff]">Top up</span>
      <span className="rounded-full bg-[#f1f1f1] py-2 text-center text-[0.5625rem] font-medium text-[#171717]">Freeze</span>
    </div>
    <span className={`${LABEL} mt-4`}>Recent</span>
    <div className="mt-1.5 flex flex-col divide-y divide-[#efefef]">
      {SPEND.map((s) => (
        <div key={s.name} className="flex items-center justify-between py-2">
          <span className="text-[0.5625rem] text-[#171717]">{s.name}</span>
          <span className={`${MONO} text-[0.5625rem] ${s.credit ? 'text-[#0f7a55]' : 'text-[#171717]'}`}>{s.amount}</span>
        </div>
      ))}
    </div>
  </div>
);

const SCREENS = [
  { key: 'home', screen: HOME_SCREEN },
  { key: 'transfer', screen: TRANSFER_SCREEN },
  { key: 'card', screen: CARD_SCREEN },
];

/* Slot 0 is the front; 1 and 2 fan out behind it to the left and right. */
const POSES = [
  { x: 0, y: 0, rotate: 0, scale: 1, z: 3, opacity: 1 },
  { x: -68, y: 28, rotate: -8, scale: 0.88, z: 2, opacity: 0.92 },
  { x: 68, y: 28, rotate: 8, scale: 0.88, z: 1, opacity: 0.92 },
];

export function PhoneFrame({ children, className = '' }) {
  return (
    <div
      className={`relative aspect-[9/19] w-[14.5rem] rounded-[2.6rem] bg-[#171717] p-[0.55rem] shadow-[0_40px_80px_-40px_rgba(15,23,20,0.55),inset_0_0_0_1px_rgba(255,255,255,0.08)] sm:w-[16rem] ${className}`}
    >
      <div className="h-full overflow-hidden rounded-[2.1rem] bg-[#ffffff]">
        <StatusBar />
        {children}
      </div>
    </div>
  );
}

const DECK_LABEL =
  'Three Yunlen app screens: the home screen with USD, EUR and USDC balances, a euro transfer with its rate and progress, and a digital card with recent spending.';

export default function PhoneDeck({ className = '' }) {
  const ref = useRef(null);
  const tick = useTicker(SCREENS.length, 3200, ref);

  return (
    <div
      ref={ref}
      role="img"
      aria-label={DECK_LABEL}
      className={`relative mx-auto flex h-[32rem] w-full max-w-[44rem] items-start justify-center pt-2 [--fan:1.6] sm:h-[37.5rem] ${className}`}
    >
      {SCREENS.map((s, i) => {
        const slot = (i - tick + SCREENS.length) % SCREENS.length;
        const pose = POSES[slot];
        return (
          <div
            key={s.key}
            aria-hidden="true"
            // Phones show one screen at a time: the fanned-out pair would crowd a 390px column.
            className={`absolute top-0 transition-[transform,opacity] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] will-change-transform ${slot === 0 ? '' : 'max-sm:!opacity-0'}`}
            style={{
              zIndex: pose.z,
              opacity: pose.opacity,
              transform: `translate3d(calc(var(--fan) * ${pose.x}px), ${pose.y}px, 0) rotate(${pose.rotate}deg) scale(${pose.scale})`,
            }}
          >
            <PhoneFrame>{s.screen}</PhoneFrame>
          </div>
        );
      })}
    </div>
  );
}
